import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { fetchPriceSummary } from "../../actions/priceActions";
import { useCart } from "../../components/CartContext";

import "./css/CheckoutScreen.css";

function getImageUrl(image) {
  if (!image) return "";

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${image}`;
}

function CheckoutScreen() {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
  } = useCart();

  const [priceSummary, setPriceSummary] = useState({
    subtotal: 0,
    tax: 0,
    serviceCharge: 0,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /*
   * =====================================================
   * FETCH PRICE SUMMARY
   * =====================================================
   */

  useEffect(() => {
    const loadPriceSummary = async () => {
      if (!cart || cart.length === 0) {
        setLoading(false);

        setPriceSummary({
          subtotal: 0,
          tax: 0,
          serviceCharge: 0,
          total: 0,
        });

        return;
      }

      try {
        setLoading(true);
        setError("");

        const items = cart.map((item) => ({
          menu_item_id: item.id,
          quantity: item.quantity,
        }));

        const response = await dispatch(
          fetchPriceSummary(
            restaurantId,
            items
          )
        );

        setPriceSummary({
          subtotal: Number(
            response?.subtotal || 0
          ),

          tax: Number(
            response?.tax || 0
          ),

          serviceCharge: Number(
            response?.service_charge ||
              response?.serviceCharge ||
              0
          ),

          total: Number(
            response?.total || 0
          ),
        });
      } catch (err) {
        console.error(
          "Price summary error:",
          err
        );

        setError(
          err.response?.data?.detail ||
            err.response?.data ||
            "Unable to calculate order total."
        );

        /*
         * Fallback to cart total
         */
        setPriceSummary({
          subtotal: Number(
            cartTotal || 0
          ),
          tax: 0,
          serviceCharge: 0,
          total: Number(
            cartTotal || 0
          ),
        });
      } finally {
        setLoading(false);
      }
    };

    loadPriceSummary();
  }, [
    restaurantId,
    cart,
    cartTotal,
    dispatch,
  ]);

  /*
   * =====================================================
   * BACK TO CART
   * =====================================================
   */

  const handleBack = () => {
    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );
  };

  /*
   * =====================================================
   * CONTINUE TO ORDER
   * =====================================================
   */

  const handleContinue = () => {
    if (!cart || cart.length === 0) {
      return;
    }

    navigate(
      `/${restaurantId}/table/${tableId}/order`,
      {
        state: {
          subtotal:
            priceSummary.subtotal,

          tax:
            priceSummary.tax,

          serviceCharge:
            priceSummary.serviceCharge,

          total:
            priceSummary.total,
        },
      }
    );
  };

  /*
   * =====================================================
   * EMPTY CART
   * =====================================================
   */

  if (!cart || cart.length === 0) {
    return (
      <div className="theme4-checkout-page">

        <div className="theme4-checkout-container">

          <button
            type="button"
            className="theme4-checkout-back"
            onClick={handleBack}
          >
            <span>←</span>
            BACK TO CART
          </button>

          <div className="theme4-checkout-empty">

            <div className="theme4-checkout-empty-icon">
              🛒
            </div>

            <span>
              CHECKOUT
            </span>

            <h1>
              YOUR CART IS EMPTY
            </h1>

            <p>
              Add some delicious dishes
              before continuing to checkout.
            </p>

            <button
              type="button"
              className="theme4-checkout-primary-button"
              onClick={() =>
                navigate(
                  `/${restaurantId}/table/${tableId}`
                )
              }
            >
              EXPLORE MENU
            </button>

          </div>

        </div>

      </div>
    );
  }

  return (
    <div className="theme4-checkout-page">

      <div className="theme4-checkout-container">

        {/* =================================================
            BACK
        ================================================= */}

        <button
          type="button"
          className="theme4-checkout-back"
          onClick={handleBack}
        >
          <span>←</span>
          BACK TO CART
        </button>

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="theme4-checkout-heading">

          <span className="theme4-checkout-eyebrow">
            STEP 01
          </span>

          <h1>
            CHECKOUT
          </h1>

          <p>
            Review your order before continuing.
          </p>

        </div>

        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <div className="theme4-checkout-layout">

          {/* =================================================
              LEFT — ITEMS
          ================================================= */}

          <div className="theme4-checkout-items-section">

            <div className="theme4-checkout-section-heading">

              <div>
                <span>
                  YOUR SELECTION
                </span>

                <h2>
                  ORDER ITEMS
                </h2>
              </div>

              <span className="theme4-checkout-item-count">
                {cartCount}{" "}
                {cartCount === 1
                  ? "ITEM"
                  : "ITEMS"}
              </span>

            </div>

            <div className="theme4-checkout-items">

              {cart.map((item, index) => {

                const image =
                  item.image ||
                  item.image_url ||
                  item.photo;

                const itemTotal =
                  Number(item.price || 0) *
                  Number(item.quantity || 0);

                return (
                  <div
                    className="theme4-checkout-item"
                    key={item.id}
                  >

                    {/* IMAGE */}

                    <div className="theme4-checkout-item-image-wrapper">

                      {image ? (
                        <img
                          src={getImageUrl(image)}
                          alt={item.name}
                          className="theme4-checkout-item-image"
                        />
                      ) : (
                        <div className="theme4-checkout-item-image-placeholder">
                          <span>✦</span>
                        </div>
                      )}

                      <span className="theme4-checkout-item-number">
                        {String(index + 1).padStart(
                          2,
                          "0"
                        )}
                      </span>

                    </div>

                    {/* DETAILS */}

                    <div className="theme4-checkout-item-details">

                      <h3>
                        {item.name}
                      </h3>

                      {item.description && (
                        <p>
                          {item.description}
                        </p>
                      )}

                      <div className="theme4-checkout-item-meta">

                        <div className="theme4-checkout-quantity">

                          <button
                            type="button"
                            onClick={() =>
                              decreaseQuantity(
                                item.id
                              )
                            }
                          >
                            −
                          </button>

                          <span>
                            {item.quantity}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              increaseQuantity(
                                item.id
                              )
                            }
                          >
                            +
                          </button>

                        </div>

                        <span>
                          ₹
                          {itemTotal.toFixed(
                            2
                          )}
                        </span>

                      </div>

                      <button
                        type="button"
                        className="theme4-checkout-remove"
                        onClick={() =>
                          removeFromCart(
                            item.id
                          )
                        }
                      >
                        REMOVE
                      </button>

                    </div>

                  </div>
                );
              })}

            </div>

          </div>

          {/* =================================================
              RIGHT — SUMMARY
          ================================================= */}

          <aside className="theme4-checkout-summary">

            <div className="theme4-checkout-summary-top">

              <span>
                ORDER SUMMARY
              </span>

              <div className="theme4-checkout-summary-line" />

            </div>

            {/* SUBTOTAL */}

            <div className="theme4-checkout-summary-row">

              <span>
                SUBTOTAL
              </span>

              <strong>
                ₹
                {priceSummary.subtotal.toFixed(
                  2
                )}
              </strong>

            </div>

            {/* TAX */}

            <div className="theme4-checkout-summary-row">

              <span>
                TAX
              </span>

              <strong>
                ₹
                {priceSummary.tax.toFixed(
                  2
                )}
              </strong>

            </div>

            {/* SERVICE CHARGE */}

            <div className="theme4-checkout-summary-row">

              <span>
                SERVICE CHARGE
              </span>

              <strong>
                ₹
                {priceSummary.serviceCharge.toFixed(
                  2
                )}
              </strong>

            </div>

            {/* DIVIDER */}

            <div className="theme4-checkout-summary-divider" />

            {/* TOTAL */}

            <div className="theme4-checkout-total-row">

              <div>

                <span>
                  TOTAL
                </span>

                <small>
                  INCLUDING ALL CHARGES
                </small>

              </div>

              <strong>
                ₹
                {priceSummary.total.toFixed(
                  2
                )}
              </strong>

            </div>

            {/* ERROR */}

            {error && (
              <div className="theme4-checkout-error">
                {typeof error === "string"
                  ? error
                  : "Unable to calculate order total."}
              </div>
            )}

            {/* CONTINUE */}

            <button
              type="button"
              className="theme4-checkout-primary-button theme4-checkout-continue-button"
              onClick={handleContinue}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="theme4-checkout-spinner" />
                  CALCULATING...
                </>
              ) : (
                <>
                  CONTINUE TO ORDER
                  <span>→</span>
                </>
              )}
            </button>

            <p className="theme4-checkout-secure-text">
              Your order will be reviewed on the
              next step before it is created.
            </p>

          </aside>

        </div>

      </div>

    </div>
  );
}

export default CheckoutScreen;